import { IconHelpCircle } from "@tabler/icons-react";
import { useState } from "react";
import { DateDisplay } from "~/components/datetime";
import { OrgLink } from "~/components/OrgLink";
import { useActionCenter } from "~/contexts/actionCenterProvider";
import { OrgUserDisplay } from "~/modules/org/org-user";
import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Button,
  Flex,
  Group,
  Modal,
  Paper,
  Stack,
  showNotification,
  Text,
  Textarea,
  Tooltip,
} from "~/ui";
import { api } from "~/utils/api";
import { enumTermToText } from "~/utils/strings";
import { getOrderApprovalActionTransition } from "./approval-action-payload";
import { MAX_APPROVAL_NOTE_LENGTH, type OrderApprovalDecisionResult, type OrderApprovalRequestHistory } from "./approval-request-contract";

const outcomeMessage = (result: OrderApprovalDecisionResult, targeted = false): string => {
  const outcome = result.actionOutcome;
  if (outcome.kind === "advanced") {
    if (targeted) {
      return `Selected shipment quantities processed. Order status: ${outcome.status.toLowerCase()}.${outcome.message ? ` ${outcome.message}` : ""}`;
    }
    return `Order advanced to ${outcome.status.toLowerCase()}.${outcome.message ? ` ${outcome.message}` : ""}`;
  }
  if (outcome.kind === "blocked") {
    return `Approval recorded. Action blocked: ${outcome.message} A new attempt requires a fresh request and approval.`;
  }
  if (outcome.kind === "unknown") {
    return outcome.message;
  }
  if (!result.changed) {
    return "This request already has a recorded outcome. No action was attempted.";
  }
  return result.status === "APPROVED"
    ? "Approval recorded. Other current requirements still need decisions."
    : `Request ${result.status.toLowerCase()}.`;
};

const ApprovalRequestCard = ({
  request,
  onUpdate,
  inbox = false,
}: {
  request: OrderApprovalRequestHistory;
  onUpdate?: () => void;
  inbox?: boolean;
}) => {
  const utils = api.useUtils();
  const [decision, setDecision] = useState<"approve" | "reject" | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OrderApprovalDecisionResult | null>(null);
  const refresh = async () => {
    await Promise.all([utils.orders.invalidate(), utils.inbox.invalidate()]);
    onUpdate?.();
  };
  const decide = api.orders.approvals.decide.useMutation();
  const rescind = api.orders.approvals.rescind.useMutation();
  const busy = decide.isPending || rescind.isPending;
  const save = async () => {
    if (!decision) {
      return;
    }
    setError(null);
    try {
      const response = await decide.mutateAsync({ requestId: request.id, decision, note });
      setResult(response);
      showNotification({
        title: "Approval request",
        message: outcomeMessage(response, request.fulfillmentMode === "TARGETED"),
        autoClose: false,
      });
      setDecision(null);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to save this decision.");
      await refresh();
    }
  };
  const rescindRequest = async () => {
    setError(null);
    try {
      setResult(await rescind.mutateAsync({ requestId: request.id }));
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to rescind this request.");
      await refresh();
    }
  };
  const actions = (request.capabilities.canDecide || request.capabilities.canRescind) && (
    <Group spacing="xs" noWrap={!inbox}>
      {request.capabilities.canDecide && (
        <>
          <Button
            size="xs"
            disabled={busy}
            onClick={() => {
              setDecision("approve");
              setNote("");
              setError(null);
            }}
          >
            Approve
          </Button>
          <Button
            size="xs"
            color="red"
            variant="outline"
            disabled={busy}
            onClick={() => {
              setDecision("reject");
              setNote("");
              setError(null);
            }}
          >
            Reject
          </Button>
        </>
      )}
      {request.capabilities.canRescind && (
        <Button size="xs" variant="subtle" loading={rescind.isPending} disabled={busy} onClick={() => void rescindRequest()}>
          Rescind request
        </Button>
      )}
    </Group>
  );
  return (
    <Paper withBorder radius="sm" p="sm" style={{ overflowWrap: "anywhere" }}>
      <Stack spacing={4}>
        <Flex justify="space-between" gap="xs" wrap={!inbox && request.status === "PENDING" ? "wrap-reverse" : "nowrap"} align="center">
          <Text size="sm" fw={600} style={{ flex: "1 1 auto", minWidth: 0 }}>
            {request.message || request.ruleLabel}
          </Text>
          <Group spacing="xs" noWrap style={{ flexShrink: 0, marginLeft: "auto" }}>
            {!inbox && actions}
            {(!inbox || request.status !== "PENDING") && (
              <Badge size="sm" color={request.status === "APPROVED" ? "green" : request.status === "REJECTED" ? "red" : "gray"}>
                {request.status.toLowerCase()}
              </Badge>
            )}
          </Group>
        </Flex>
        {inbox && actions}
        <Text size="xs" color="dimmed">
          Requested by <OrgUserDisplay idOrUser={request.requestedById} hideAvatar span /> ·{" "}
          <DateDisplay value={request.requestedAt} useOrgTimezone span />
          {request.requestNote && ` — Request Note: “${request.requestNote}”`}
        </Text>
        {(request.decidedAt || request.decisionNote) && (
          <Text size="xs" color="dimmed">
            {request.decidedAt && (
              <>
                {request.status === "REJECTED" ? "Rejected" : "Approved"} by{" "}
                <OrgUserDisplay idOrUser={request.decidedById} hideAvatar span /> ·{" "}
                <DateDisplay value={request.decidedAt} useOrgTimezone span />
              </>
            )}
            {request.decisionNote && ` — Decision Note: “${request.decisionNote}”`}
          </Text>
        )}
        {request.closedAt && (
          <Text size="xs" color="dimmed">
            Closed: {request.closeReason?.toLowerCase().replaceAll("_", " ")} · <DateDisplay value={request.closedAt} useOrgTimezone span />
          </Text>
        )}
        {result && (
          <Alert
            color={result.actionOutcome.kind === "blocked" || result.actionOutcome.kind === "unknown" ? "orange" : "blue"}
            role="status"
          >
            {outcomeMessage(result, request.fulfillmentMode === "TARGETED")}
          </Alert>
        )}
        {error && !decision && (
          <Alert color="red" role="alert">
            {error}
          </Alert>
        )}
      </Stack>
      <Modal
        opened={decision !== null}
        title={
          <Group spacing="xs" noWrap>
            {decision === "reject" ? "Reject approval request" : "Approve request"}
            <Tooltip
              label={
                decision === "reject"
                  ? "Rejecting leaves the order at its current status."
                  : request.fulfillmentMode === "TARGETED"
                    ? "Once approved, only the saved shipment line quantities will be attempted. " +
                      "The whole order may remain partially fulfilled."
                    : `Once this request is approved, order will attempt to move to ${enumTermToText(getOrderApprovalActionTransition(request.action).toStatus)}.`
              }
              multiline
              width={280}
              withArrow
              events={{ hover: true, focus: true, touch: true }}
            >
              <ActionIcon variant="subtle" color="gray" size="sm" aria-label="What happens next">
                <IconHelpCircle size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        }
        onClose={() => {
          if (!busy) {
            setDecision(null);
          }
        }}
      >
        <Stack spacing="sm">
          <Text size="sm">
            Requested by <OrgUserDisplay idOrUser={request.requestedById} hideAvatar span />
          </Text>
          {request.requestNote && (
            <Text size="sm" style={{ overflowWrap: "anywhere" }}>
              Request Note: {request.requestNote}
            </Text>
          )}
          <Textarea
            data-autofocus
            label="Decision Note"
            required={decision === "reject"}
            value={note}
            maxLength={MAX_APPROVAL_NOTE_LENGTH}
            onChange={event => setNote(event.currentTarget.value)}
          />
          {error && (
            <Alert color="red" role="alert">
              {error}
            </Alert>
          )}
          <Group position="right">
            <Button variant="default" disabled={busy} onClick={() => setDecision(null)}>
              Cancel
            </Button>
            <Button
              color={decision === "reject" ? "red" : undefined}
              loading={decide.isPending}
              disabled={decision === "reject" && note.trim().length === 0}
              onClick={() => void save()}
            >
              {decision === "reject" ? "Reject" : "Approve"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
};

export const OrderApprovalHistory = ({ orderId }: { orderId: string }) => {
  const detail = api.orders.approvals.detail.useQuery({ orderId });
  if (detail.isError) {
    return (
      <Alert color="red">
        Unable to load approval history.{" "}
        <Button variant="subtle" onClick={() => void detail.refetch()}>
          Retry
        </Button>
      </Alert>
    );
  }
  if (!detail.data?.requests.length) {
    return null;
  }
  return (
    <Accordion
      key={orderId}
      mt="md"
      chevronPosition="left"
      defaultValue={detail.data.requests.some(request => request.status === "PENDING") ? "approvals" : undefined}
    >
      <Accordion.Item value="approvals">
        <Accordion.Control>
          <Group spacing="md">
            <Text fw={700}>Approvals</Text>
            <Text>{detail.data.requests.length}</Text>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack spacing="xs">
            {detail.data.requests.map(request => (
              <ApprovalRequestCard key={request.id} request={request} />
            ))}
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

/** Approval Tasks never expose ordinary Task mutations, including on terminal Inbox rows. */
export const InboxApprovalRequest = ({
  orderId,
  requestId,
  title,
  onUpdate,
}: {
  orderId: string;
  requestId: string;
  title: string;
  onUpdate: () => void;
}) => {
  const actionCenter = useActionCenter();
  const detail = api.orders.approvals.detail.useQuery({ orderId });
  const order = api.orders.get.useQuery({ id: orderId }, { select: value => value.orderNo });
  const request = detail.data?.requests.find(row => row.id === requestId);
  return (
    <Stack p="sm" spacing={4}>
      <Text fw={600}>{title}</Text>
      {order.data && (
        <OrgLink href={`/orders/${orderId}`} onClick={() => actionCenter.setIsOpen(false)}>
          <Text size="sm" fw={600}>
            {order.data}
          </Text>
        </OrgLink>
      )}
      {request ? (
        <ApprovalRequestCard request={request} onUpdate={onUpdate} inbox />
      ) : (
        <Text size="sm">
          {detail.isError ? "Approval details are unavailable. Open the order to refresh your access." : "Loading approval…"}
        </Text>
      )}
    </Stack>
  );
};
